import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getCompanyUploadedFileSignedUrl,
  previewAllPaymentPlatformFiles,
  previewPaymentPlatformImport,
  registerPaymentPlatformFile,
  startPaymentPlatformImport,
} from "../api/companyApi";
import PaymentPlatformColumnMappingDialog from "./PaymentPlatformColumnMappingDialog";
import PaymentPlatformDataCenter from "./PaymentPlatformDataCenter";
import PaymentPlatformFileUploadDialog from "./PaymentPlatformFileUploadDialog";

export default function PaymentPlatformDataCenterWorkspace({
  tenantId,
  title = "Data center",
  description,
  uploadLabel,
  uploadDialogTitle,
  uploadDialogDescription,
  source = "dashboard_upload",
}) {
  const fileInput = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pendingImport, setPendingImport] = useState(null);
  const [mappingPreview, setMappingPreview] = useState(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const chooseFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) setSelectedFiles(files);
  };

  const uploadFiles = async (labelledFiles) => {
    setBusy(true);
    try {
      const uploaded = [];
      for (const { file, labels } of labelledFiles) {
        const signed = await getCompanyUploadedFileSignedUrl(tenantId, {
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
        });
        await axios.put(signed.data.uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        const catalogFile = await registerPaymentPlatformFile(tenantId, {
          fileGCSKey: signed.data.fileGCSKey,
          fileName: signed.data.originalFileName || file.name,
          labels,
          source,
        });
        uploaded.push(catalogFile);
      }
      // Keep the automatic post-upload run scoped to the files selected in this
      // interaction. The explicit "Map columns" action below is the only place
      // that intentionally combines the tenant's historical data-center files.
      const preview = await previewPaymentPlatformImport(tenantId, uploaded);
      setPendingImport({ files: uploaded, preview });
      setSelectedFiles([]);
      setRefreshKey((value) => value + 1);
      toast.success("Files added. Map columns for this upload.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "File upload failed");
    } finally {
      setBusy(false);
    }
  };

  const mapAllFiles = async () => {
    setBusy(true);
    try {
      const preview = await previewAllPaymentPlatformFiles(tenantId);
      if (!preview?.files?.length) {
        toast.error("Upload at least one file before mapping columns.");
        return;
      }
      setMappingPreview(preview);
      setMappingOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not inspect tenant files");
    } finally {
      setBusy(false);
    }
  };

  const applyMapping = async (columnMapping) => {
    const files = pendingImport?.files || mappingPreview?.files;
    if (!files?.length) return;
    setBusy(true);
    try {
      await startPaymentPlatformImport(tenantId, files, null, columnMapping);
      setPendingImport(null);
      setMappingOpen(false);
      toast.success("Mapping saved. Assessments are rebuilding from all data-center files.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not rebuild assessments");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        hidden
        multiple
        ref={fileInput}
        type="file"
        accept=".csv,.xls,.xlsx"
        onChange={chooseFiles}
      />
      <PaymentPlatformDataCenter
        tenantId={tenantId}
        refreshKey={refreshKey}
        busy={busy}
        title={title}
        description={description}
        uploadLabel={uploadLabel}
        onUpload={() => fileInput.current?.click()}
        onMapColumns={mapAllFiles}
      />
      <PaymentPlatformFileUploadDialog
        open={selectedFiles.length > 0}
        files={selectedFiles}
        busy={busy}
        title={uploadDialogTitle}
        description={uploadDialogDescription}
        onClose={() => setSelectedFiles([])}
        onUpload={uploadFiles}
      />
      <PaymentPlatformColumnMappingDialog
        open={Boolean(pendingImport) || mappingOpen}
        preview={pendingImport?.preview || mappingPreview}
        currentMapping={null}
        busy={busy}
        onClose={() => {
          setPendingImport(null);
          setMappingOpen(false);
        }}
        onApply={applyMapping}
        onSaveDraft={null}
      />
    </>
  );
}
